/**
 * Build POST /buy-sources/search body from Analyzer or Storefront Hunter product.
 *
 * Product Analyzer keeps Search Sources as an explicit user action.
 * Storefront Hunter may call this automatically via a bounded queue after catalog render.
 * The already-loaded Walmart product supplies enough context for the lightweight
 * Google Shopping lookup — no extra Walmart fetch is required here.
 */

function numericPrice(value) {
  if (value == null || value === "") return null;

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  const cleaned = String(value)
    .replace(/[$,\s]/g, "")
    .trim();

  if (!cleaned) return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function buildBuySourcesSearchBody(product, postalCode) {
  if (!product) return {};

  const w = product.walmart || {};
  const body = {};

  const itemId =
    w.item_id_clean ||
    w.walmart_item_id ||
    product.walmart_item_id ||
    (product.id != null && String(product.id).match(/^\d{5,12}$/)
      ? product.id
      : null);

  if (itemId != null && itemId !== "") {
    body.walmart_item_id = String(itemId).trim();
  }

  const gtin =
    product.gtin ||
    product.upc ||
    w.gtin ||
    w.upc ||
    w.gtin14;

  if (gtin != null && gtin !== "") {
    const digits = String(gtin).replace(/\D/g, "");
    if (digits) body.gtin = digits;
  }

  const title =
    product.title ||
    product.product_title ||
    product.productTitle ||
    product.name ||
    w.title ||
    w.product_title ||
    w.productTitle;

  if (title != null && String(title).trim()) {
    body.title = String(title).trim();
  }

  const brand =
    product.brand ||
    w.brand ||
    product.brand_name ||
    product.brandName ||
    w.brand_name ||
    w.brandName;

  if (brand != null && String(brand).trim()) {
    body.brand = String(brand).trim();
  }

  // Buy Box is preferred when already known. Otherwise the Storefront Walmart
  // listing price is our reference price for this lightweight comparison.
  const walmartTargetPrice = numericPrice(
    w.buy_box_price ??
      w.buyBoxPrice ??
      product.buy_box_price ??
      product.buyBoxPrice ??
      product.storefront_price ??
      product.storefrontPrice ??
      product.price ??
      w.price
  );

  if (walmartTargetPrice != null) {
    body.walmart_target_price = walmartTargetPrice;
  }

  const zip = String(postalCode || "").trim();
  if (zip) {
    body.postal_code = zip;
  }

  return body;
}
